import PublicLayout from '@/layouts/PublicLayout'
import SeoHead from '@/components/SeoHead'

const DSAR_URL = '/privacy/dsar'

export default function PrivacyPage() {
  return (
    <>
      <SeoHead
        title="Privacy Policy — LendSolo"
        description="How LendSolo collects, uses, shares, and protects your personal information. Last updated March 31, 2026."
        canonicalPath="/privacy"
        ogType="article"
      />

      <PublicLayout
        title="Privacy Policy"
        description="Last updated March 31, 2026"
      >
        <div
          className="prose prose-gray max-w-none
            prose-headings:font-bold prose-headings:text-gray-900 prose-headings:scroll-mt-16
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:leading-relaxed prose-p:text-gray-700
            prose-li:text-gray-700
            prose-a:text-emerald-600 prose-a:font-medium hover:prose-a:text-emerald-700
            prose-strong:text-gray-900"
        >
          <p>
            This Privacy Notice for Lendsolo LLC ("<strong>we</strong>," "<strong>us</strong>,"
            or "<strong>our</strong>"), describes how and why we might access, collect, store,
            use, and/or share ("<strong>process</strong>") your personal information when you
            use our services ("<strong>Services</strong>"), including when you:
          </p>

          <ul>
            <li>
              Visit our website at{' '}
              <a href="https://www.lendsolo.com" target="_blank" rel="noopener noreferrer">
                https://www.lendsolo.com
              </a>{' '}
              or any website of ours that links to this Privacy Notice
            </li>
            <li>
              <p>
                Use LendSolo. LendSolo is a cloud-based loan portfolio management platform
                designed for independent private lenders and micro-lending businesses. The
                application enables users to track private loans, record and categorize
                payments, generate amortization schedules, manage borrower records, produce
                tax documents (including IRS Form 1098), and monitor portfolio performance
                through dashboards and reports.
              </p>
              <p>
                Users create an account with their email address and password, then enter
                information about their lending business and individual loans. The platform
                collects and processes the following types of data:
              </p>
              <p>
                User account data: name, email address, password (encrypted), business name,
                business address, subscription plan, and payment information (processed by
                Stripe — we do not store credit card numbers).
              </p>
              <p>
                Borrower data: borrower names, email addresses, mailing addresses, and
                taxpayer identification numbers (TINs). TINs are encrypted at rest using
                Rails built-in encryption and are used solely for generating IRS Form 1098
                interest reporting documents.
              </p>
              <p>
                Financial data: loan principal amounts, interest rates, loan terms, payment
                amounts, payment dates, principal/interest splits, expense records, and
                capital transaction history.
              </p>
              <p>
                Usage data: session cookies for authentication, email notification
                preferences, and subscription status.
              </p>
              <p>
                The platform integrates with the following third-party services that may
                process user data:
              </p>
              <ul>
                <li>Stripe — payment processing for subscriptions</li>
                <li>
                  Resend — transactional email delivery (payment reminders, late notices,
                  receipts, monthly portfolio summaries, borrower interest statements)
                </li>
                <li>
                  Anthropic — AI-powered features (deal memo generation and risk narratives,
                  available on the Pro plan; loan data is sent to the Anthropic API for
                  processing but is not retained by Anthropic for training)
                </li>
                <li>Render — cloud hosting and managed PostgreSQL database</li>
              </ul>
              <p>
                The application sends automated emails on behalf of the user to their
                borrowers (payment reminders, late payment notices, payment receipts, and
                annual interest statements). Users can control which email types are enabled
                through their account settings.
              </p>
              <p>
                LendSolo does not sell, rent, or share user data with third parties for
                marketing purposes. Data is used solely to provide the described services.
              </p>
            </li>
            <li>Engage with us in other related ways, including any marketing or events</li>
          </ul>

          <p>
            <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you
            understand your privacy rights and choices. We are responsible for making
            decisions about how your personal information is processed. If you do not agree
            with our policies and practices, please do not use our Services. If you still
            have any questions or concerns, please contact us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>

          <h2>SUMMARY OF KEY POINTS</h2>
          <p>
            <strong>
              <em>
                This summary provides key points from our Privacy Notice, but you can find
                out more details about any of these topics by clicking the link following
                each key point or by using our{' '}
                <a href="#toc">table of contents</a> below to find the section you are
                looking for.
              </em>
            </strong>
          </p>

          <p>
            <strong>What personal information do we process?</strong> When you visit, use, or
            navigate our Services, we may process personal information depending on how you
            interact with us and the Services, the choices you make, and the products and
            features you use. Learn more about{' '}
            <a href="#personalinfo">personal information you disclose to us</a>.
          </p>

          <p>
            <strong>Do we process any sensitive personal information?</strong> Some of the
            information may be considered "special" or "sensitive" in certain jurisdictions,
            for example your racial or ethnic origins, sexual orientation, and religious
            beliefs. We may process sensitive personal information when necessary with your
            consent or as otherwise permitted by applicable law. Learn more about{' '}
            <a href="#sensitiveinfo">sensitive information we process</a>.
          </p>

          <p>
            <strong>Do we collect any information from third parties?</strong> We do not
            collect any information from third parties.
          </p>

          <p>
            <strong>How do we process your information?</strong> We process your information
            to provide, improve, and administer our Services, communicate with you, for
            security and fraud prevention, and to comply with law. We may also process your
            information for other purposes with your consent. We process your information
            only when we have a valid legal reason to do so. Learn more about{' '}
            <a href="#infouse">how we process your information</a>.
          </p>

          <p>
            <strong>
              In what situations and with which parties do we share personal information?
            </strong>{' '}
            We may share information in specific situations and with specific third parties.
            Learn more about{' '}
            <a href="#whoshare">
              when and with whom we share your personal information
            </a>
            .
          </p>

          <p>
            <strong>How do we keep your information safe?</strong> We have adequate
            organizational and technical processes and procedures in place to protect your
            personal information. However, no electronic transmission over the internet or
            information storage technology can be guaranteed to be 100% secure, so we cannot
            promise or guarantee that hackers, cybercriminals, or other unauthorized third
            parties will not be able to defeat our security and improperly collect, access,
            steal, or modify your information. Learn more about{' '}
            <a href="#infosafe">how we keep your information safe</a>.
          </p>

          <p>
            <strong>What are your rights?</strong> Depending on where you are located
            geographically, the applicable privacy law may mean you have certain rights
            regarding your personal information. Learn more about{' '}
            <a href="#privacyrights">your privacy rights</a>.
          </p>

          <p>
            <strong>How do you exercise your rights?</strong> The easiest way to exercise
            your rights is by submitting a{' '}
            <a href={DSAR_URL}>
              data subject access request
            </a>
            , or by contacting us. We will consider and act upon any request in accordance
            with applicable data protection laws.
          </p>

          <p>
            Want to learn more about what we do with any information we collect?{' '}
            <a href="#toc">Review the Privacy Notice in full</a>.
          </p>

          <h2 id="toc">TABLE OF CONTENTS</h2>
          <ol>
            <li>
              <a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>
            </li>
            <li>
              <a href="#infouse">HOW DO WE PROCESS YOUR INFORMATION?</a>
            </li>
            <li>
              <a href="#whoshare">
                WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
              </a>
            </li>
            <li>
              <a href="#cookies">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</a>
            </li>
            <li>
              <a href="#ai">DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</a>
            </li>
            <li>
              <a href="#inforetain">HOW LONG DO WE KEEP YOUR INFORMATION?</a>
            </li>
            <li>
              <a href="#infosafe">HOW DO WE KEEP YOUR INFORMATION SAFE?</a>
            </li>
            <li>
              <a href="#infominors">DO WE COLLECT INFORMATION FROM MINORS?</a>
            </li>
            <li>
              <a href="#privacyrights">WHAT ARE YOUR PRIVACY RIGHTS?</a>
            </li>
            <li>
              <a href="#DNT">CONTROLS FOR DO-NOT-TRACK FEATURES</a>
            </li>
            <li>
              <a href="#uslaws">DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</a>
            </li>
            <li>
              <a href="#clausea">THIRD-PARTY DATA PROCESSED ON BEHALF OF USERS</a>
            </li>
            <li>
              <a href="#policyupdates">DO WE MAKE UPDATES TO THIS NOTICE?</a>
            </li>
            <li>
              <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>
            </li>
            <li>
              <a href="#request">
                HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
              </a>
            </li>
          </ol>

          <h2 id="infocollect">1. WHAT INFORMATION DO WE COLLECT?</h2>

          <h3 id="personalinfo">Personal information you disclose to us</h3>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>{' '}
            <em>We collect personal information that you provide to us.</em>
          </p>
          <p>
            We collect personal information that you voluntarily provide to us when you
            register on the Services, express an interest in obtaining information about us
            or our products and Services, when you participate in activities on the
            Services, or otherwise when you contact us.
          </p>
          <p>
            <strong>Personal Information Provided by You.</strong> The personal information
            that we collect depends on the context of your interactions with us and the
            Services, the choices you make, and the products and features you use. The
            personal information we collect may include the following:
          </p>
          <ul>
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>mailing addresses</li>
            <li>contact preferences</li>
            <li>taxpayer identification numbers (tins)</li>
          </ul>

          <p id="sensitiveinfo">
            <strong>Sensitive Information.</strong> When necessary, with your consent or as
            otherwise permitted by applicable law, we process the following categories of
            sensitive information:
          </p>
          <ul>
            <li>social security numbers or other government identifiers</li>
            <li>financial data</li>
          </ul>

          <p>
            <strong>Payment Data.</strong> We may collect data necessary to process your
            payment if you choose to make purchases, such as your payment instrument number,
            and the security code associated with your payment instrument. All payment data
            is handled and stored by Stripe. You may find their privacy notice link(s)
            here:{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              https://stripe.com/privacy
            </a>
            .
          </p>
          <p>
            All personal information that you provide to us must be true, complete, and
            accurate, and you must notify us of any changes to such personal information.
          </p>

          <h2 id="infouse">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
          <p>
            <strong>
              <em>In Short: </em>
            </strong>
            <em>
              We process your information to provide, improve, and administer our Services,
              communicate with you, for security and fraud prevention, and to comply with
              law. We may also process your information for other purposes with your
              consent.
            </em>
          </p>
          <p>
            <strong>
              We process your personal information for a variety of reasons, depending on
              how you interact with our Services, including:
            </strong>
          </p>
          <ul>
            <li>
              <strong>
                To facilitate account creation and authentication and otherwise manage user
                accounts.
              </strong>{' '}
              We may process your information so you can create and log in to your account,
              as well as keep your account in working order.
            </li>
            <li>
              <strong>
                To deliver and facilitate delivery of services to the user.
              </strong>{' '}
              We may process your information to provide you with the requested service.
            </li>
            <li>
              <strong>To send administrative information to you.</strong> We may process
              your information to send you details about our products and services, changes
              to our <a href="/terms">terms</a> and policies, and other similar information.
            </li>
            <li>
              <strong>To request feedback.</strong> We may process your information when
              necessary to request feedback and to contact you about your use of our
              Services.
            </li>
            <li>
              <strong>To protect our Services.</strong> We may process your information as
              part of our efforts to keep our Services safe and secure, including fraud
              monitoring and prevention.
            </li>
            <li>
              <strong>To comply with our legal obligations.</strong> We may process your
              information to comply with our legal obligations, respond to legal requests,
              and exercise, establish, or defend our legal rights.
            </li>
            <li>
              <strong>To send tax forms.</strong> To generate tax documents (IRS Form 1098)
              and financial reports on behalf of users
            </li>
          </ul>

          <h2 id="whoshare">
            3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </h2>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>
            <em>
              {' '}
              We may share information in specific situations described in this section
              and/or with the following third parties.
            </em>
          </p>
          <p>We may need to share your personal information in the following situations:</p>
          <ul>
            <li>
              <strong>Business Transfers.</strong> We may share or transfer your information
              in connection with, or during negotiations of, any merger, sale of company
              assets, financing, or acquisition of all or a portion of our business to
              another company.
            </li>
          </ul>

          <h2 id="cookies">4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>
            <em>
              {' '}
              We may use cookies and other tracking technologies to collect and store your
              information.
            </em>
          </p>
          <p>
            We may use cookies and similar tracking technologies (like web beacons and
            pixels) to gather information when you interact with our Services. Some online
            tracking technologies help us maintain the security of our Services and your
            account, prevent crashes, fix bugs, save your preferences, and assist with basic
            site functions.
          </p>
          <p>
            We also permit third parties and service providers to use online tracking
            technologies on our Services for analytics and advertising, including to help
            manage and display advertisements, to tailor advertisements to your interests,
            or to send abandoned shopping cart reminders (depending on your communication
            preferences). The third parties and service providers use their technology to
            provide advertising about products and services tailored to your interests which
            may appear either on our Services or on other websites.
          </p>
          <p>
            To the extent these online tracking technologies are deemed to be a
            "sale"/"sharing" (which includes targeted advertising, as defined under the
            applicable laws) under applicable US state laws, you can opt out of these online
            tracking technologies by submitting a request as described below under section "
            <a href="#uslaws">DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</a>"
          </p>
          <p>
            Specific information about how we use such technologies and how you can refuse
            certain cookies is set out in our Cookie Notice:{' '}
            <a
              href="https://lendsolo.com/cookies"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://lendsolo.com/cookies
            </a>
            .
          </p>

          <h2 id="ai">5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</h2>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>
            <em>
              {' '}
              We offer products, features, or tools powered by artificial intelligence,
              machine learning, or similar technologies.
            </em>
          </p>
          <p>
            As part of our Services, we offer products, features, or tools powered by
            artificial intelligence, machine learning, or similar technologies
            (collectively, "AI Products"). These tools are designed to enhance your
            experience and provide you with innovative solutions. The terms in this Privacy
            Notice govern your use of the AI Products within our Services.
          </p>

          <h3>Use of AI Technologies</h3>
          <p>
            We provide the AI Products through third-party service providers ("AI Service
            Providers"), including Anthropic. As outlined in this Privacy Notice, your
            input, output, and personal information will be shared with and processed by
            these AI Service Providers to enable your use of our AI Products for purposes
            outlined in "
            <a href="#whoshare">
              WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </a>
            " You must not use the AI Products in any way that violates the terms or
            policies of any AI Service Provider.
          </p>

          <h3>Our AI Products</h3>
          <p>Our AI Products are designed for the following functions:</p>
          <ul>
            <li>AI development</li>
            <li>AI automation</li>
            <li>Text analysis</li>
          </ul>

          <h3>How We Process Your Data Using AI</h3>
          <p>
            All personal information processed using our AI Products is handled in line with
            our Privacy Notice and our agreement with third parties. This ensures high
            security and safeguards your personal information throughout the process, giving
            you peace of mind about your data's safety.
          </p>

          <h3>How to Opt Out</h3>
          <p>We believe in giving you the power to decide how your data is used. To opt out, you can:</p>
          <ul>
            <li>Log in to your account settings and update your user account</li>
            <li>Contact us using the contact information provided</li>
          </ul>

          <h2 id="inforetain">6. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
          <p>
            <strong>
              <em>In Short: </em>
            </strong>
            <em>
              We keep your information for as long as necessary to fulfill the purposes
              outlined in this Privacy Notice unless otherwise required by law.
            </em>
          </p>
          <p>
            We will only keep your personal information for as long as it is necessary for
            the purposes set out in this Privacy Notice, unless a longer retention period is
            required or permitted by law (such as tax, accounting, or other legal
            requirements). No purpose in this notice will require us keeping your personal
            information for longer than six (6) months past the termination of the user's
            account.
          </p>
          <p>
            When we have no ongoing legitimate business need to process your personal
            information, we will either delete or anonymize such information, or, if this is
            not possible (for example, because your personal information has been stored in
            backup archives), then we will securely store your personal information and
            isolate it from any further processing until deletion is possible.
          </p>

          <h2 id="infosafe">7. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
          <p>
            <strong>
              <em>In Short: </em>
            </strong>
            <em>
              We aim to protect your personal information through a system of organizational
              and technical security measures.
            </em>
          </p>
          <p>
            We have implemented appropriate and reasonable technical and organizational
            security measures designed to protect the security of any personal information
            we process. However, despite our safeguards and efforts to secure your
            information, no electronic transmission over the Internet or information storage
            technology can be guaranteed to be 100% secure, so we cannot promise or
            guarantee that hackers, cybercriminals, or other unauthorized third parties will
            not be able to defeat our security and improperly collect, access, steal, or
            modify your information. Although we will do our best to protect your personal
            information, transmission of personal information to and from our Services is at
            your own risk. You should only access the Services within a secure environment.
          </p>

          <h2 id="infominors">8. DO WE COLLECT INFORMATION FROM MINORS?</h2>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>
            <em> We do not knowingly collect data from or market to children under 18 years of age.</em>
          </p>
          <p>
            We do not knowingly collect, solicit data from, or market to children under 18
            years of age, nor do we knowingly sell such personal information. By using the
            Services, you represent that you are at least 18 or that you are the parent or
            guardian of such a minor and consent to such minor dependent's use of the
            Services. If we learn that personal information from users less than 18 years of
            age has been collected, we will deactivate the account and take reasonable
            measures to promptly delete such data from our records. If you become aware of
            any data we may have collected from children under age 18, please contact us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>

          <h2 id="privacyrights">9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
          <p>
            <strong>
              <em>In Short:</em>
            </strong>
            <em>
              {' '}
              You may review, change, or terminate your account at any time, depending on
              your country, province, or state of residence.
            </em>
          </p>

          <p id="withdrawconsent">
            <strong>
              <u>Withdrawing your consent:</u>
            </strong>{' '}
            If we are relying on your consent to process your personal information, which
            may be express and/or implied consent depending on the applicable law, you have
            the right to withdraw your consent at any time. You can withdraw your consent at
            any time by contacting us by using the contact details provided in the section "
            <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>" below.
          </p>
          <p>
            However, please note that this will not affect the lawfulness of the processing
            before its withdrawal nor, when applicable law allows, will it affect the
            processing of your personal information conducted in reliance on lawful
            processing grounds other than consent.
          </p>

          <h3>Account Information</h3>
          <p>
            If you would at any time like to review or change the information in your
            account or terminate your account, you can:
          </p>
          <ul>
            <li>Log in to your account settings and update your user account.</li>
            <li>Contact us using the contact information provided.</li>
          </ul>
          <p>
            Upon your request to terminate your account, we will deactivate or delete your
            account and information from our active databases. However, we may retain some
            information in our files to prevent fraud, troubleshoot problems, assist with
            any investigations, enforce our <a href="/terms">legal terms</a> and/or comply
            with applicable legal requirements.
          </p>
          <p>
            <strong>
              <u>Cookies and similar technologies:</u>
            </strong>{' '}
            Most Web browsers are set to accept cookies by default. If you prefer, you can
            usually choose to set your browser to remove cookies and to reject cookies. If
            you choose to remove cookies or reject cookies, this could affect certain
            features or services of our Services. For further information, please see our
            Cookie Notice:{' '}
            <a
              href="https://lendsolo.com/cookies"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://lendsolo.com/cookies
            </a>
            .
          </p>
          <p>
            If you have questions or comments about your privacy rights, you may email us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>

          <h2 id="DNT">10. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
          <p>
            Most web browsers and some mobile operating systems and mobile applications
            include a Do-Not-Track ("DNT") feature or setting you can activate to signal
            your privacy preference not to have data about your online browsing activities
            monitored and collected. At this stage, no uniform technology standard for
            recognizing and implementing DNT signals has been finalized. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If a standard
            for online tracking is adopted that we must follow in the future, we will inform
            you about that practice in a revised version of this Privacy Notice.
          </p>
          <p>
            California law requires us to let you know how we respond to web browser DNT
            signals. Because there currently is not an industry or legal standard for
            recognizing or honoring DNT signals, we do not respond to them at this time.
          </p>

          <h2 id="uslaws">11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
          <p>
            <strong>
              <em>In Short: </em>
            </strong>
            <em>
              If you are a resident of California, Colorado, Connecticut, Delaware, Florida,
              Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New
              Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or
              Virginia, you may have the right to request access to and receive details
              about the personal information we maintain about you and how we have processed
              it, correct inaccuracies, get a copy of, or delete your personal information.
              You may also have the right to withdraw your consent to our processing of your
              personal information. These rights may be limited in some circumstances by
              applicable law. More information is provided below.
            </em>
          </p>

          <h3>Categories of Personal Information We Collect</h3>
          <p>
            The table below shows the categories of personal information we have collected
            in the past twelve (12) months. The table includes illustrative examples of each
            category and does not reflect the personal information we collect from you. For
            a comprehensive inventory of all personal information we process, please refer
            to the section "<a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>"
          </p>

          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 p-3 text-left">Category</th>
                <th className="border border-gray-300 p-3 text-left">Examples</th>
                <th className="border border-gray-300 p-3 text-center">Collected</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-3">A. Identifiers</td>
                <td className="border border-gray-300 p-3">
                  Contact details, such as real name, alias, postal address, telephone or
                  mobile contact number, unique personal identifier, online identifier,
                  Internet Protocol address, email address, and account name
                </td>
                <td className="border border-gray-300 p-3 text-center">YES</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  B. Personal information as defined in the California Customer Records
                  statute
                </td>
                <td className="border border-gray-300 p-3">
                  Name, contact information, education, employment, employment history, and
                  financial information
                </td>
                <td className="border border-gray-300 p-3 text-center">YES</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  C. Protected classification characteristics under state or federal law
                </td>
                <td className="border border-gray-300 p-3">
                  Gender, age, date of birth, race and ethnicity, national origin, marital
                  status, and other demographic data
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">D. Commercial information</td>
                <td className="border border-gray-300 p-3">
                  Transaction information, purchase history, financial details, and payment
                  information
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">E. Biometric information</td>
                <td className="border border-gray-300 p-3">Fingerprints and voiceprints</td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  F. Internet or other similar network activity
                </td>
                <td className="border border-gray-300 p-3">
                  Browsing history, search history, online behavior, interest data, and
                  interactions with our and other websites, applications, systems, and
                  advertisements
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">G. Geolocation data</td>
                <td className="border border-gray-300 p-3">Device location</td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  H. Audio, electronic, sensory, or similar information
                </td>
                <td className="border border-gray-300 p-3">
                  Images and audio, video or call recordings created in connection with our
                  business activities
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  I. Professional or employment-related information
                </td>
                <td className="border border-gray-300 p-3">
                  Business contact details in order to provide you our Services at a
                  business level or job title, work history, and professional qualifications
                  if you apply for a job with us
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">J. Education Information</td>
                <td className="border border-gray-300 p-3">
                  Student records and directory information
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  K. Inferences drawn from collected personal information
                </td>
                <td className="border border-gray-300 p-3">
                  Inferences drawn from any of the collected personal information listed
                  above to create a profile or summary about, for example, an individual's
                  preferences and characteristics
                </td>
                <td className="border border-gray-300 p-3 text-center">NO</td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-3">
                  L. Sensitive personal Information
                </td>
                <td className="border border-gray-300 p-3">Social security numbers</td>
                <td className="border border-gray-300 p-3 text-center">YES</td>
              </tr>
            </tbody>
          </table>

          <p>
            We only collect sensitive personal information, as defined by applicable privacy
            laws or the purposes allowed by law or with your consent. Sensitive personal
            information may be used, or disclosed to a service provider or contractor, for
            additional, specified purposes. You may have the right to limit the use or
            disclosure of your sensitive personal information. We do not collect or process
            sensitive personal information for the purpose of inferring characteristics
            about you.
          </p>

          <p>
            We may also collect other personal information outside of these categories
            through instances where you interact with us in person, online, or by phone or
            mail in the context of:
          </p>
          <ul>
            <li>Receiving help through our customer support channels;</li>
            <li>Participation in customer surveys or contests; and</li>
            <li>
              Facilitation in the delivery of our Services and to respond to your inquiries.
            </li>
          </ul>

          <p>
            We will use and retain the collected personal information as needed to provide
            the Services or for:
          </p>
          <ul>
            <li>Category A - As long as the user has an account with us</li>
            <li>Category B - As long as the user has an account with us</li>
            <li>Category L - As long as the user has an account with us</li>
          </ul>

          <h3>Sources of Personal Information</h3>
          <p>
            Learn more about the sources of personal information we collect in "
            <a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>"
          </p>

          <h3>How We Use and Share Personal Information</h3>
          <p>
            Learn more about how we use your personal information in the section, "
            <a href="#infouse">HOW DO WE PROCESS YOUR INFORMATION?</a>"
          </p>

          <p>
            <strong>Will your information be shared with anyone else?</strong>
          </p>
          <p>
            We may disclose your personal information with our service providers pursuant to
            a written contract between us and each service provider. Learn more about how we
            disclose personal information to in the section, "
            <a href="#whoshare">
              WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </a>
            "
          </p>
          <p>
            We may use your personal information for our own business purposes, such as for
            undertaking internal research for technological development and demonstration.
            This is not considered to be "selling" of your personal information.
          </p>
          <p>
            We have not disclosed, sold, or shared any personal information to third parties
            for a business or commercial purpose in the preceding twelve (12) months. We
            will not sell or share personal information in the future belonging to website
            visitors, users, and other consumers.
          </p>

          <h3>Your Rights</h3>
          <p>
            You have rights under certain US state data protection laws. However, these
            rights are not absolute, and in certain cases, we may decline your request as
            permitted by law. These rights include:
          </p>
          <ul>
            <li>
              <strong>Right to know</strong> whether or not we are processing your personal
              data
            </li>
            <li>
              <strong>Right to access </strong>your personal data
            </li>
            <li>
              <strong>Right to correct </strong>inaccuracies in your personal data
            </li>
            <li>
              <strong>Right to request</strong> the deletion of your personal data
            </li>
            <li>
              <strong>Right to obtain a copy </strong>of the personal data you previously
              shared with us
            </li>
            <li>
              <strong>Right to non-discrimination</strong> for exercising your rights
            </li>
            <li>
              <strong>Right to opt out</strong> of the processing of your personal data if
              it is used for targeted advertising (or sharing as defined under California's
              privacy law), the sale of personal data, or profiling in furtherance of
              decisions that produce legal or similarly significant effects ("profiling")
            </li>
          </ul>

          <p>Depending upon the state where you live, you may also have the following rights:</p>
          <ul>
            <li>
              Right to access the categories of personal data being processed (as permitted
              by applicable law, including the privacy law in Minnesota)
            </li>
            <li>
              Right to obtain a list of the categories of third parties to which we have
              disclosed personal data (as permitted by applicable law, including the privacy
              law in California, Delaware, and Maryland)
            </li>
            <li>
              Right to obtain a list of specific third parties to which we have disclosed
              personal data (as permitted by applicable law, including the privacy law in
              Minnesota and Oregon)
            </li>
            <li>
              Right to obtain a list of third parties to which we have sold personal data
              (as permitted by applicable law, including the privacy law in Connecticut)
            </li>
            <li>
              Right to review, understand, question, and depending on where you live,
              correct how personal data has been profiled (as permitted by applicable law,
              including the privacy law in Connecticut and Minnesota)
            </li>
            <li>
              Right to limit use and disclosure of sensitive personal data (as permitted by
              applicable law, including the privacy law in California)
            </li>
            <li>
              Right to opt out of the collection of sensitive data and personal data
              collected through the operation of a voice or facial recognition feature (as
              permitted by applicable law, including the privacy law in Florida)
            </li>
          </ul>

          <h3>How to Exercise Your Rights</h3>
          <p>
            To exercise these rights, you can contact us by submitting a{' '}
            <a href={DSAR_URL}>
              data subject access request
            </a>
            , by emailing us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>, or by referring
            to the contact details at the bottom of this document.
          </p>
          <p>
            Under certain US state data protection laws, you can designate an authorized
            agent to make a request on your behalf. We may deny a request from an
            authorized agent that does not submit proof that they have been validly
            authorized to act on your behalf in accordance with applicable laws.
          </p>

          <h3>Request Verification</h3>
          <p>
            Upon receiving your request, we will need to verify your identity to determine
            you are the same person about whom we have the information in our system. We
            will only use personal information provided in your request to verify your
            identity or authority to make the request. However, if we cannot verify your
            identity from the information already maintained by us, we may request that you
            provide additional information for the purposes of verifying your identity and
            for security or fraud-prevention purposes.
          </p>
          <p>
            If you submit the request through an authorized agent, we may need to collect
            additional information to verify your identity before processing your request
            and the agent will need to provide a written and signed permission from you to
            submit such request on your behalf.
          </p>

          <h3>Appeals</h3>
          <p>
            Under certain US state data protection laws, if we decline to take action
            regarding your request, you may appeal our decision by emailing us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>. We will inform
            you in writing of any action taken or not taken in response to the appeal,
            including a written explanation of the reasons for the decisions. If your appeal
            is denied, you may submit a complaint to your state attorney general.
          </p>

          <h3>California "Shine The Light" Law</h3>
          <p>
            California Civil Code Section 1798.83, also known as the "Shine The Light" law,
            permits our users who are California residents to request and obtain from us,
            once a year and free of charge, information about categories of personal
            information (if any) we disclosed to third parties for direct marketing purposes
            and the names and addresses of all third parties with which we shared personal
            information in the immediately preceding calendar year. If you are a California
            resident and would like to make such a request, please submit your request in
            writing to us by using the contact details provided in the section "
            <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>"
          </p>

          <h2 id="clausea">12. THIRD-PARTY DATA PROCESSED ON BEHALF OF USERS</h2>
          <p>
            Third-Party Data Processed on Behalf of Users. LendSolo users may input personal
            information about third parties, including borrower names, email addresses,
            mailing addresses, and taxpayer identification numbers (TINs/SSNs), in order to
            manage loan portfolios and generate tax documents. This third-party data is
            stored and processed solely to provide the services requested by the user.
            Borrower TINs are encrypted at rest. LendSolo may send transactional emails to
            borrowers on behalf of users, including payment reminders, late payment notices,
            payment receipts, and annual interest statements, based on the user's configured
            email settings. LendSolo does not use borrower data for any purpose other than
            delivering the services described above.
          </p>

          <h2 id="policyupdates">13. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
          <p>
            <em>
              <strong>In Short: </strong>Yes, we will update this notice as necessary to
              stay compliant with relevant laws.
            </em>
          </p>
          <p>
            We may update this Privacy Notice from time to time. The updated version will be
            indicated by an updated "Revised" date at the top of this Privacy Notice. If we
            make material changes to this Privacy Notice, we may notify you either by
            prominently posting a notice of such changes or by directly sending you a
            notification. We encourage you to review this Privacy Notice frequently to be
            informed of how we are protecting your information.
          </p>

          <h2 id="contact">14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
          <p>
            If you have questions or comments about this notice, you may email us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>
          <p>Lendsolo LLC</p>

          <h2 id="request">
            15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </h2>
          <p>
            Based on the applicable laws of your country or state of residence in the US,
            you may have the right to request access to the personal information we collect
            from you, details about how we have processed it, correct inaccuracies, or
            delete your personal information. You may also have the right to withdraw your
            consent to our processing of your personal information. These rights may be
            limited in some circumstances by applicable law. To request to review, update,
            or delete your personal information, please fill out and submit a{' '}
            <a href={DSAR_URL}>
              data subject access request
            </a>
            .
          </p>
        </div>
      </PublicLayout>
    </>
  )
}
